import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, me, clearToken, getToken } from "@/lib/adminApi";
import ContentEditor from "@/pages/admin/ContentEditor";
import TrendingProjectsTab from "@/pages/admin/TrendingProjectsTab";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Home,
  Users,
  FileText,
  Building2,
  ExternalLink,
  LayoutDashboard,
  Flame,
} from "lucide-react";

const AVAILABILITY_OPTIONS = ["Available", "Reserved", "Sold", "Coming Soon"];

const emptyProperty = {
  name: "",
  developer: "",
  starting_price: "",
  payment_plan: "",
  completion: "",
  audience: "",
  investment_highlights: "",
  lifestyle_highlights: "",
  image_url: "",
  availability: "Available",
  is_active: true,
  sort_order: 0,
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("properties");

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    me()
      .then(setUser)
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    nav("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-[#616161]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/10 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-serif-luxe text-lg text-[#1D1D1F]" data-testid="admin-brand">
              DXB <span className="text-[#B68D40]">Property Zone</span>
              <span className="ml-3 text-xs tracking-[0.2em] uppercase text-[#616161]">Admin</span>
            </a>
          </div>
          <div className="flex items-center gap-5">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-[#616161] hover:text-[#B68D40] inline-flex items-center gap-2" data-testid="admin-view-site">
              <ExternalLink size={14} strokeWidth={1.4} /> View Site
            </a>
            <div className="text-sm text-[#616161] hidden sm:block">{user.email}</div>
            <button onClick={logout} className="btn-outline-dark !py-2 !px-4 !text-[0.7rem]" data-testid="admin-logout">
              <LogOut size={14} strokeWidth={1.4} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 w-full">
        <div className="flex gap-2 border-b border-black/10 mb-8 overflow-x-auto">
          {[
            { k: "properties", label: "Properties", icon: Building2 },
            { k: "trending", label: "Trending Projects", icon: Flame },
            { k: "content", label: "Page Content", icon: LayoutDashboard },
            { k: "leads", label: "Leads", icon: Users },
            { k: "insights", label: "Insight Requests", icon: FileText },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm tracking-[0.1em] uppercase transition-all border-b-2 -mb-px ${
                tab === t.k
                  ? "border-[#B68D40] text-[#1D1D1F]"
                  : "border-transparent text-[#616161] hover:text-[#1D1D1F]"
              }`}
            >
              <t.icon size={14} strokeWidth={1.4} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "properties" && <PropertiesTab />}
        {tab === "trending" && <TrendingProjectsTab />}
        {tab === "content" && <ContentEditor />}
        {tab === "leads" && <LeadsTab />}
        {tab === "insights" && <InsightsTab />}
      </div>
    </div>
  );
}

/* -------- Properties Tab -------- */
function PropertiesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // property object or "new"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/properties");
      setItems(data);
    } catch {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`);
      toast.success("Property deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-luxe text-3xl text-[#1D1D1F]">Property Listings</h2>
          <p className="text-sm text-[#616161] mt-2">Add, edit, and manage properties shown on the landing page.</p>
        </div>
        <button
          onClick={() => setEditing({ ...emptyProperty, sort_order: (items[items.length - 1]?.sort_order || 0) + 1 })}
          className="btn-luxe"
          data-testid="admin-new-property"
        >
          <Plus size={16} strokeWidth={1.4} /> New Property
        </button>
      </div>

      {loading ? (
        <div className="text-[#616161] py-16 text-center">Loading properties…</div>
      ) : items.length === 0 ? (
        <div className="text-[#616161] py-16 text-center border border-dashed border-black/15">No properties yet. Click &ldquo;New Property&rdquo; to add one.</div>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white p-5 flex gap-5 shadow-[0_4px_20px_rgba(0,0,0,0.04)]" data-testid={`admin-property-row-${p.id}`}>
              <img src={p.image_url} alt={p.name} className="w-32 h-24 object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif-luxe text-xl text-[#1D1D1F]">{p.name}</h3>
                      <span className={`text-[0.65rem] tracking-[0.14em] uppercase px-2.5 py-1 ${
                        p.availability === "Available" ? "bg-[#B68D40] text-white" :
                        p.availability === "Reserved" ? "bg-[#1D1D1F] text-white" :
                        p.availability === "Sold" ? "bg-black/10 text-[#1D1D1F]" :
                        "bg-white text-[#1D1D1F] border border-[#B68D40]/50"
                      }`}>
                        {p.availability}
                      </span>
                      {!p.is_active && (
                        <span className="text-[0.65rem] tracking-[0.14em] uppercase px-2.5 py-1 bg-red-100 text-red-800">Hidden</span>
                      )}
                    </div>
                    <p className="text-sm text-[#616161] mt-1">{p.developer}</p>
                    <div className="flex gap-4 text-xs text-[#616161] mt-2">
                      <span><b className="text-[#1D1D1F]">{p.starting_price}</b></span>
                      <span>{p.payment_plan}</span>
                      <span>Handover: {p.completion}</span>
                      <span>Order: {p.sort_order}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setEditing(p)}
                      className="w-9 h-9 flex items-center justify-center border border-[#1D1D1F] text-[#1D1D1F] hover:bg-[#1D1D1F] hover:text-white transition-all"
                      title="Edit"
                      data-testid={`admin-edit-${p.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.4} />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="w-9 h-9 flex items-center justify-center border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all"
                      title="Delete"
                      data-testid={`admin-delete-${p.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.4} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <PropertyEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/* -------- Property Editor Modal -------- */
function PropertyEditor({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...emptyProperty, ...initial });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order, 10) || 0,
      };
      if (isNew) {
        await api.post("/admin/properties", payload);
        toast.success("Property created");
      } else {
        await api.put(`/admin/properties/${initial.id}`, payload);
        toast.success("Property updated");
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-property-editor"
    >
      <div
        className="bg-white w-full max-w-3xl my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <h3 className="font-serif-luxe text-2xl text-[#1D1D1F]">
            {isNew ? "New Property" : "Edit Property"}
          </h3>
          <button onClick={onClose} className="text-[#616161] hover:text-[#1D1D1F]" data-testid="admin-editor-close">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-x-6 gap-y-5" data-testid="admin-editor-form">
          <Field label="Property Name" required>
            <input required className="input-luxe" value={form.name} onChange={update("name")} data-testid="pe-name" />
          </Field>
          <Field label="Developer" required>
            <input required className="input-luxe" value={form.developer} onChange={update("developer")} data-testid="pe-developer" />
          </Field>
          <Field label="Starting Price" required>
            <input required className="input-luxe" placeholder="AED 5.4M" value={form.starting_price} onChange={update("starting_price")} data-testid="pe-price" />
          </Field>
          <Field label="Payment Plan" required>
            <input required className="input-luxe" placeholder="60/40 · 3-Year" value={form.payment_plan} onChange={update("payment_plan")} data-testid="pe-plan" />
          </Field>
          <Field label="Completion / Handover" required>
            <input required className="input-luxe" placeholder="Q4 2027 or Ready" value={form.completion} onChange={update("completion")} data-testid="pe-completion" />
          </Field>
          <Field label="Availability">
            <select className="input-luxe" value={form.availability} onChange={update("availability")} data-testid="pe-availability">
              {AVAILABILITY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>

          <Field label="Image URL" required span={2}>
            <input required type="url" className="input-luxe" placeholder="https://…" value={form.image_url} onChange={update("image_url")} data-testid="pe-image" />
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="w-full h-40 object-cover mt-3" />
            )}
          </Field>

          <Field label="Suitable For (Audience)" required span={2}>
            <input required className="input-luxe" value={form.audience} onChange={update("audience")} data-testid="pe-audience" />
          </Field>
          <Field label="Investment Highlights" required span={2}>
            <input required className="input-luxe" value={form.investment_highlights} onChange={update("investment_highlights")} data-testid="pe-invest" />
          </Field>
          <Field label="Offers" required span={2}>
            <input required className="input-luxe" value={form.lifestyle_highlights} onChange={update("lifestyle_highlights")} data-testid="pe-lifestyle" />
          </Field>

          <Field label="Sort Order">
            <input type="number" className="input-luxe" value={form.sort_order} onChange={update("sort_order")} data-testid="pe-order" />
          </Field>
          <Field label="Visible on Landing Page">
            <label className="flex items-center gap-3 pt-2">
              <input type="checkbox" checked={form.is_active} onChange={update("is_active")} data-testid="pe-active" />
              <span className="text-sm text-[#616161]">{form.is_active ? "Active — visible" : "Hidden"}</span>
            </label>
          </Field>

          <div className="md:col-span-2 flex justify-end gap-3 mt-4 pt-6 border-t border-black/10">
            <button type="button" onClick={onClose} className="btn-outline-dark" data-testid="admin-editor-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-luxe" data-testid="admin-editor-save">
              <Save size={14} strokeWidth={1.4} /> {saving ? "Saving…" : "Save Property"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, span = 1, required }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">
        {label} {required && <span className="text-[#B68D40]">·</span>}
      </label>
      {children}
    </div>
  );
}

/* -------- Leads Tab -------- */
function LeadsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/leads").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  return (
    <div>
      <h2 className="font-serif-luxe text-3xl text-[#1D1D1F] mb-6">Consultation Leads ({items.length})</h2>
      {loading ? <div className="text-[#616161]">Loading…</div> :
        items.length === 0 ? <div className="text-[#616161] border border-dashed border-black/15 p-8 text-center">No leads yet.</div> :
        <div className="bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="text-left border-b border-black/10">
                <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Country</Th><Th>Budget</Th><Th>Objective</Th><Th>Preferred</Th><Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-black/5 hover:bg-[#FAFAF8]" data-testid={`admin-lead-${l.id}`}>
                  <Td>{l.full_name}</Td>
                  <Td><a href={`mailto:${l.email}`} className="link-gold">{l.email}</a></Td>
                  <Td>{l.phone}</Td>
                  <Td>{l.country}</Td>
                  <Td>{l.investment_budget}</Td>
                  <Td>{l.investment_objective}</Td>
                  <Td>{l.preferred_consultation}</Td>
                  <Td className="text-[#616161] text-xs">{new Date(l.created_at).toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

/* -------- Insights Tab -------- */
function InsightsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/insights").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  return (
    <div>
      <h2 className="font-serif-luxe text-3xl text-[#1D1D1F] mb-6">Insight Requests ({items.length})</h2>
      {loading ? <div className="text-[#616161]">Loading…</div> :
        items.length === 0 ? <div className="text-[#616161] border border-dashed border-black/15 p-8 text-center">No requests yet.</div> :
        <div className="bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-insights-table">
            <thead>
              <tr className="text-left border-b border-black/10">
                <Th>Resource</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-black/5 hover:bg-[#FAFAF8]">
                  <Td>{l.resource}</Td>
                  <Td>{l.name || "—"}</Td>
                  <Td><a href={`mailto:${l.email}`} className="link-gold">{l.email}</a></Td>
                  <Td>{l.phone ? <a href={`tel:${l.phone}`} className="link-gold">{l.phone}</a> : "—"}</Td>
                  <Td className="text-[#616161] text-xs">{new Date(l.created_at).toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

const Th = ({ children }) => <th className="py-3 px-4 text-[0.65rem] tracking-[0.14em] uppercase text-[#616161] font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3 px-4 text-[#1D1D1F] ${className}`}>{children}</td>;
